import { useState } from 'react';
import {
  useDeleteOperation, useLeverageOperations, useSendSignal, useSetExecuted, useSetOperationState, useSignalSettings,
} from '@/hooks/leverageQueries';
import { useToast } from '@/hooks/useToast';
import { CardSkeleton, ConfirmDialog, ErrorState, StatLine } from '@/components/ui';
import { IconPlus, IconSend } from '@/components/Icons';
import { formatDate } from '@/lib/format';
import { DIRECTION_LABELS } from '@/lib/leverage';
import { formatBRLShort, formatNumber } from '@/lib/format';
import type { LeverageAllocation, LeverageOperation, LeverageOperationStatus } from '@/lib/types';
import { OperationCard } from './OperationCard';
import { OperationModal } from './OperationModal';
import { SignalSettingsModal } from './SignalSettingsModal';

const FILTERS: { value: LeverageOperationStatus | ''; label: string }[] = [
  { value: 'OPEN', label: 'Abertas' },
  { value: 'CLOSED', label: 'Encerradas' },
  { value: '', label: 'Todas' },
];

type Pending =
  | { kind: 'edit' | 'close' | 'delete'; operation: LeverageOperation }
  | { kind: 'signal'; operation: LeverageOperation; again: boolean }
  | { kind: 'create' }
  | { kind: 'settings' };

/** As operações do administrador: montar, executar cliente a cliente, encerrar. */
export function OperationsPanel() {
  const toast = useToast();
  const [status, setStatus] = useState<LeverageOperationStatus | ''>('OPEN');
  const [pending, setPending] = useState<Pending | null>(null);
  const { data, isLoading, error } = useLeverageOperations({ status: status || undefined });
  const setState = useSetOperationState();
  const remove = useDeleteOperation();
  const executed = useSetExecuted();
  const sendSignal = useSendSignal();
  const { data: signalSettings } = useSignalSettings();

  if (error) return <ErrorState message={(error as Error).message} />;

  const operations = data?.items ?? [];
  const toExecute = operations.reduce((sum, item) => sum + (item.status === 'OPEN' ? item.totals.clients - item.totals.executed : 0), 0);
  const amount = operations.reduce((sum, item) => sum + item.totals.amount, 0);
  const close = () => setPending(null);

  async function run(action: () => Promise<unknown>, message: string) {
    try {
      await action();
      toast.success(message);
      close();
    } catch (caught) {
      toast.fromError(caught);
    }
  }

  async function toggleExecuted(operation: LeverageOperation, allocation: LeverageAllocation) {
    try {
      await executed.mutateAsync({ operationId: operation.id, allocationId: allocation.id, executed: !allocation.executedAt });
    } catch (caught) {
      toast.fromError(caught);
    }
  }

  /** Sem configuração pronta (ou com o token vencido), abre a configuração em vez de enviar. */
  function askSignal(operation: LeverageOperation, again: boolean) {
    if (!signalSettings?.ready || signalSettings.expired) {
      toast.error(signalSettings?.expired ? 'O token da corretora venceu: cole um novo.' : 'Configure os sinais da corretora antes de enviar.');
      return setPending({ kind: 'settings' });
    }
    setPending({ kind: 'signal', operation, again });
  }

  async function reopen(operation: LeverageOperation) {
    try {
      await setState.mutateAsync({ id: operation.id, action: 'reopen' });
      toast.success(`${operation.symbol} reaberta.`);
    } catch (caught) {
      toast.fromError(caught);
    }
  }

  return (
    <>
      <div className="toolbar">
        <div className="lev-filter" role="group" aria-label="Situação">
          {FILTERS.map((filter) => (
            <button
              key={filter.label}
              type="button"
              className="lev-filter-btn"
              aria-pressed={status === filter.value}
              onClick={() => setStatus(filter.value)}
            >
              {filter.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="btn"
          style={{ marginLeft: 'auto' }}
          onClick={() => setPending({ kind: 'settings' })}
          title="Token, api-key e slot da api de sinais da corretora"
        >
          <IconSend /> Sinais
          {signalSettings && (!signalSettings.ready || signalSettings.expired) ? <span className="chip chip-warning">{signalSettings.expired ? 'token vencido' : 'configurar'}</span> : null}
        </button>
        <button type="button" className="btn btn-primary" onClick={() => setPending({ kind: 'create' })}>
          <IconPlus /> Novo sinal
        </button>
      </div>

      {data ? (
        <div className="metric-row" style={{ marginBottom: 28 }}>
          <StatLine label="Operações" value={formatNumber(operations.length)} sub={FILTERS.find((filter) => filter.value === status)?.label.toLowerCase()} />
          <StatLine label="A entrar" value={formatBRLShort(amount)} color="var(--emphasis)" sub="soma das fatias" />
          <StatLine
            label="Falta executar"
            value={formatNumber(toExecute)}
            color={toExecute > 0 ? 'var(--warning)' : undefined}
            sub={toExecute === 1 ? 'cliente' : 'clientes, nas abertas'}
          />
        </div>
      ) : null}

      {isLoading && !data ? (
        <div style={{ display: 'grid', gap: 16 }}><CardSkeleton height={220} /><CardSkeleton height={220} /></div>
      ) : operations.length === 0 ? (
        <div className="empty">
          <p style={{ margin: '0 0 14px' }}>{status === 'OPEN' ? 'Nenhuma operação aberta.' : 'Nenhuma operação com esse filtro.'}</p>
          <button type="button" className="btn btn-primary" onClick={() => setPending({ kind: 'create' })}>
            <IconPlus /> Novo sinal
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 18 }}>
          {operations.map((operation) => (
            <OperationCard
              key={operation.id}
              operation={operation}
              pendingAllocationId={executed.isPending ? executed.variables?.allocationId ?? null : null}
              onToggleExecuted={(allocation) => void toggleExecuted(operation, allocation)}
              onEdit={() => setPending({ kind: 'edit', operation })}
              onClose={() => setPending({ kind: 'close', operation })}
              onReopen={() => void reopen(operation)}
              onDelete={() => setPending({ kind: 'delete', operation })}
              onSendSignal={(again) => askSignal(operation, again)}
              sendingSignal={sendSignal.isPending && sendSignal.variables?.id === operation.id}
            />
          ))}
        </div>
      )}

      {pending?.kind === 'create' ? <OperationModal onClose={close} /> : null}
      {pending?.kind === 'edit' ? <OperationModal operation={pending.operation} onClose={close} /> : null}
      {pending?.kind === 'settings' ? <SignalSettingsModal onClose={close} /> : null}
      {pending?.kind === 'signal' ? (
        <ConfirmDialog
          title={`${pending.again ? 'Enviar de novo' : 'Enviar'} o sinal de ${pending.operation.symbol}?`}
          message={
            `${DIRECTION_LABELS[pending.operation.direction]} em ${formatDate(pending.operation.date)} às ${pending.operation.time}, ` +
            `expiração de ${pending.operation.expiryMinutes} min. O sinal sai na corretora agora` +
            (pending.again ? ', e ela fica com dois: o anterior continua lá.' : '.')
          }
          confirmLabel={pending.again ? 'Enviar de novo' : 'Enviar sinal'}
          busy={sendSignal.isPending}
          onCancel={close}
          onConfirm={() =>
            void run(() => sendSignal.mutateAsync({ id: pending.operation.id, again: pending.again }), `Sinal de ${pending.operation.symbol} enviado.`)
          }
        />
      ) : null}
      {pending?.kind === 'close' ? (
        <ConfirmDialog
          title={`Encerrar ${pending.operation.symbol}?`}
          message={
            pending.operation.totals.executed < pending.operation.totals.clients
              ? `Ainda há ${pending.operation.totals.clients - pending.operation.totals.executed} sem execução marcada. Encerrar libera a fatia de todos os clientes para as próximas operações; dá para reabrir depois.`
              : 'A fatia de cada cliente volta a ficar livre para as próximas operações. Dá para reabrir depois.'
          }
          confirmLabel="Encerrar"
          busy={setState.isPending}
          onCancel={close}
          onConfirm={() => void run(() => setState.mutateAsync({ id: pending.operation.id, action: 'close' }), 'Operação encerrada.')}
        />
      ) : null}
      {pending?.kind === 'delete' ? (
        <ConfirmDialog
          title={`Excluir ${pending.operation.symbol}?`}
          message="Nenhum cliente foi executado ainda. A operação e as fatias saem."
          confirmLabel="Excluir"
          danger
          busy={remove.isPending}
          onCancel={close}
          onConfirm={() => void run(() => remove.mutateAsync(pending.operation.id), 'Operação excluída.')}
        />
      ) : null}
    </>
  );
}
